import { makeObservable, observable } from 'mobx';

import { TableCellEntity, TableCellEntityMode } from 'entities/TableCell';
import { ErrorEntity } from 'entities/common/Error';
import {
  CellControllerKind,
  TableControllersEntity,
} from 'entities/TableControllers';
import { ILocalStore } from 'stores/interfaces';

import { ITableFactoryRowModel } from '../table/TableFactoryRowModel/interface';

import { TableDateController } from './TableDateController';
import { TableInputNumberController } from './TableInputNumberController';
import { TableInputStringController } from './TableInputStringController';
import { TableNavigationController } from './TableNavigationController';
import { TableNumberController } from './TableNumberController';
import { TableProgressBarController } from './TableProgressBarController';
import { TableSelectController } from './TableSelectController';
import { TableSwitchController } from './TableSwitchController';
import { TableTextController } from './TableTextController';
import { ITableControllerBase } from './interface';

export type TableCellControllerParams<
  T extends TableControllersEntity = TableControllersEntity
> = TableCellEntity<T> & {
  parentRowModel: ITableFactoryRowModel | null;
};

export class TableCellController<
  T extends TableControllersEntity = TableControllersEntity
> implements ILocalStore
{
  private _errors: ErrorEntity[] | null;

  constructor(params: TableCellControllerParams<T>) {
    this.params = params;
    this._errors = params.errors;
    this.buildCollection(params);
    this._handleSave = this._handleSave.bind(this);
    this.buildCollection = this.buildCollection.bind(this);

    makeObservable(this, {
      controller: observable.ref,
      _errors: observable.ref,
    });
  }

  controller: ITableControllerBase;

  readonly params: TableCellControllerParams<T>;

  get errors(): ErrorEntity[] | null {
    return this._errors;
  }

  get controllerDisabled(): boolean {
    return this.params.mode === TableCellEntityMode.disabled;
  }

  private async _handleSave(): Promise<void> {
    if (!this.params.parentRowModel?.entity?.rowId) {
      return;
    }

    await this.params.parentRowModel.updateCell({
      cellId: this.params.fieldId,
      cellValue: String(this.controller?.toServerFormat ?? ''),
      rowId: this.params.parentRowModel.entity?.rowId,
    });
  }

  update(record: TableCellControllerParams): void {
    this._errors = record.errors;
    this.controller.setValue(record.controller.value);
  }

  buildCollection(record: TableCellControllerParams): void {
    switch (record.controllerKind) {
      case CellControllerKind.TEXT:
        this.controller = new TableTextController(record);

        return;
      case CellControllerKind.INPUT_DATE:
        this.controller = new TableDateController({
          record,
          onSave: () => this._handleSave(),
        });

        return;

      case CellControllerKind.SWITCH:
        this.controller = new TableSwitchController({
          record,
          onSave: () => this._handleSave(),
        });

        return;

      case CellControllerKind.PROGRESS_BAR:
        this.controller = new TableProgressBarController({
          record,
          onSave: () => this._handleSave(),
        });

        return;

      case CellControllerKind.INPUT_STRING:
        this.controller = new TableInputStringController({
          record,
          onSave: () => this._handleSave(),
        });

        return;

      case CellControllerKind.SELECT:
        this.controller = new TableSelectController({
          record,
          onSave: () => this._handleSave(),
        });

        return;

      case CellControllerKind.INPUT_NUMBER:
        this.controller = new TableInputNumberController({
          record,
          onSave: () => this._handleSave(),
        });

        return;

      case CellControllerKind.NUMBER:
        this.controller = new TableNumberController(record);

        return;

      case CellControllerKind.NAVIGATION:
        this.controller = new TableNavigationController(record);

        return;
    }
  }

  destroy() {
    this.controller.destroy();
  }
}
